import { X, Shield, Check, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface CreateAncestryDIDModalProps {
  onClose: () => void;
  onConfirm: (createDID: boolean) => void;
  memberData: {
    name: string;
    species: string;
    breed?: string;
  };
  relationship: string;
  relationshipDisplayLabel?: string;
}

export function CreateAncestryDIDModal({
  onClose,
  onConfirm,
  memberData,
  relationship,
  relationshipDisplayLabel,
}: CreateAncestryDIDModalProps) {
  const { t } = useTranslation();
  const displayRelationship = relationshipDisplayLabel ?? relationship;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl border dark:border-white/8 max-w-2xl w-full">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-700 dark:to-purple-700 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2
                className="text-2xl mb-1"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('family.modals.createAncestryDidTitle')}
              </h2>
              <p
                className="text-sm opacity-90"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('family.modals.optionalRecommended')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="bg-gray-50 dark:bg-[#0F0E16] border border-gray-200 dark:border-white/20 rounded-lg p-4 mb-6">
            <div
              className="text-sm text-gray-600 dark:text-white/70 mb-2"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t('family.modals.creatingAncestryDidFor')}
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-full flex items-center justify-center text-2xl">
                {memberData.species === 'Dog'
                  ? '🐕'
                  : memberData.species === 'Cat'
                    ? '🐈'
                    : '🐾'}
              </div>
              <div>
                <div
                  className="mb-1 text-gray-900 dark:text-white"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {memberData.name}
                </div>
                <div
                  className="text-sm text-gray-600 dark:text-white/70"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {memberData.breed || memberData.species} •{' '}
                  {displayRelationship}
                </div>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h3
              className="text-lg mb-3 flex items-center gap-2 text-gray-900 dark:text-white"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              <Shield className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              {t('family.modals.whatIsAncestryDid')}
            </h3>
            <p
              className="text-sm text-gray-700 dark:text-white/70 mb-4"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t('family.modals.ancestryDidDescription')}
            </p>

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                </div>
                <div className="text-sm">
                  <div
                    className="text-gray-900 dark:text-white mb-1"
                    style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
                  >
                    {t('family.modals.createsBlockchainRecord')}
                  </div>
                  <div
                    className="text-xs text-gray-600 dark:text-white/70"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('family.modals.generatesUniqueDid')}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                </div>
                <div className="text-sm">
                  <div
                    className="text-gray-900 dark:text-white mb-1"
                    style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
                  >
                    {t('family.modals.futureProofLinking')}
                  </div>
                  <div
                    className="text-xs text-gray-600 dark:text-white/70"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('family.modals.canBeLinkedWhenOwnerJoins')}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                </div>
                <div className="text-sm">
                  <div
                    className="text-gray-900 dark:text-white mb-1"
                    style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
                  >
                    {t('family.modals.maintainsLineageContinuity')}
                  </div>
                  <div
                    className="text-xs text-gray-600 dark:text-white/70"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('family.modals.ensuresTreeComplete')}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                </div>
                <div className="text-sm">
                  <div
                    className="text-gray-900 dark:text-white mb-1"
                    style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
                  >
                    {t('family.modals.noOwnerRequired')}
                  </div>
                  <div
                    className="text-xs text-gray-600 dark:text-white/70"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('family.modals.noOwnerRequiredDesc')}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Comparison */}
          <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-500/30 rounded-lg p-4 mb-6">
            <div
              className="text-sm text-indigo-900 dark:text-indigo-100 mb-3"
              style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
            >
              <Info className="w-4 h-4 inline mr-1" />
              Ancestry DID vs Manual Entry
            </div>
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div>
                <div
                  className="text-indigo-900 dark:text-indigo-200 mb-2"
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
                >
                  🔒 With Ancestry DID
                </div>
                <ul
                  className="text-indigo-700 dark:text-indigo-300 space-y-1"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <li>• Blockchain verified</li>
                  <li>• Can be claimed/linked later</li>
                  <li>• Part of verifiable ancestry</li>
                  <li>• Future DNA matching</li>
                </ul>
              </div>
              <div>
                <div
                  className="text-indigo-900 dark:text-indigo-200 mb-2"
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
                >
                  ✍️ Without Ancestry DID
                </div>
                <ul
                  className="text-indigo-700 dark:text-indigo-300 space-y-1"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <li>• Personal record only</li>
                  <li>• Not verifiable</li>
                  <li>• Private to your account</li>
                  <li>• Can add DID later</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Data Structure Info */}
          <div className="bg-gray-50 dark:bg-[#0F0E16] border border-gray-200 dark:border-white/20 rounded-lg p-4 mb-6">
            <div
              className="text-sm text-gray-700 dark:text-white/90 mb-2"
              style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
            >
              Ancestry DID Data Structure
            </div>
            <div
              className="text-xs text-gray-600 dark:text-white/70 space-y-1"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              <div>
                • <span className="font-mono">DID ID:</span> Auto-generated on
                Solana
              </div>
              <div>
                • <span className="font-mono">Relationship:</span>{' '}
                {relationship}
              </div>
              <div>
                • <span className="font-mono">Source:</span> Owner-added
              </div>
              <div>
                • <span className="font-mono">Verification:</span> Unverified
                (upgradeable)
              </div>
              <div>
                • <span className="font-mono">Privacy:</span> Controlled by you
              </div>
            </div>
          </div>

          {/* Privacy Notice */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-500/30 rounded-lg p-4 mb-6">
            <div
              className="text-sm text-yellow-900 dark:text-yellow-100"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              <div className="mb-1" style={{ fontWeight: 600 }}>
                🔐 Privacy & Control
              </div>
              <div className="text-xs text-yellow-700 dark:text-yellow-300">
                Ancestry DIDs are private by default. You control who can view
                this lineage information. The DID can be made public only if you
                choose to share it.
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => onConfirm(true)}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-700 dark:to-purple-700 text-white px-6 py-3 rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              <Shield className="w-5 h-5" />
              {t('family.modals.createAncestryDidRecommended')}
            </button>
            <button
              onClick={() => onConfirm(false)}
              className="w-full border border-gray-300 dark:border-white/20 bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white px-6 py-3 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              {t('family.modals.skipForNow')}
            </button>
          </div>

          <div
            className="mt-4 text-center text-xs text-gray-500 dark:text-white/50"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('family.modals.createLaterFromProfile')}
          </div>
        </div>
      </div>
    </div>
  );
}
